const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const { actionUtils, config } = require('@sstdev/lib_metadata-config');
const dataAccessMethods = require('../../../../dataAccessMethods');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../../helpers/convertToBoolean');

const TOOLTIP = 'Button that will expand a menu to allow selecting other actions from said menu';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    let workspace = blocklyBlock.workspace;
    let blockId = blocklyBlock.id;

    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged vertically.
    //Meaning each (dummy)input starts a new line
    blocklyBlock.setInputsInline(false);

    line1OfInputs
        .appendField('  Namespace:')
        .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace')
        .appendField('  Relation:')
        .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)), 'relation')
        .appendField('  use Grid Selection:')
        .appendField(new Blockly.FieldCheckbox(false), 'withSelection');

    // Set button style
    let styleDropDown = new Blockly.FieldDropdown(config.buttonStyles);
    styleDropDown.setValue('InLine');
    const line2OfInput = blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Button Style (old):')
        .appendField(styleDropDown, 'buttonStyle')
        .appendField('  Button Size (new):')
        .appendField(new Blockly.FieldDropdown(config.componentSize), 'size')
        .appendField('  Button Variant (new):')
        .appendField(new Blockly.FieldDropdown(config.buttonVariants), 'variant')
        .appendField('  Icon Name:')
        .appendField(new Blockly.FieldTextInput(''), 'iconName');

    line2OfInput
        .appendField('  Shortcut')
        .appendField(new Blockly.FieldTextInput(), 'shortcutKey')
        .appendField('  Shift Button')
        .appendField(new Blockly.FieldCheckbox(), 'shiftButton')
        .appendField('  Control Button')
        .appendField(new Blockly.FieldCheckbox(), 'controlButton')
        .appendField('  Alt Button')
        .appendField(new Blockly.FieldCheckbox(), 'altButton');

    blocklyBlock.appendStatementInput('children').setCheck('menuSelection').appendField('Menu', 'childrenField');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    // These are most common, but we overwrite/clear for specific HNodes

    //nsAndRel
    addCodeProperties(['namespace', 'relation', { key: 'withSelection', handler: convertToBoolean }]);

    //Button Display
    addCodeProperties([{ key: 'style', value: 'buttonStyle' }, 'size', 'variant', 'iconName']);

    //shortcut
    addCodeProperties([
        'shortcutKey',
        { key: 'shiftButton', handler: convertToBoolean },
        { key: 'controlButton', handler: convertToBoolean },
        { key: 'altButton', handler: convertToBoolean }
    ]);

    return code;
}

/** Builds relation metadata for the MenuButton action and returns [subDataDictionary, partialTypeDefinition]. */
function asDataDictionary(hNode, context = {}, parentAccessRestrictions) {
    const { namespace, relation, withSelection } = hNode;

    const accessRestrictions = actionUtils.getNodeAccessRestrictions(hNode, context, parentAccessRestrictions);
    if (accessRestrictions.hidden) return [{}, {}];

    // MenuButton doesn't have a forAction - it's a container for menu items that may have their own actions
    // Default access context allows GET only since menu buttons themselves don't perform CRUD
    const accessContext = {
        get: { value: true },
        post: { value: false },
        patch: { value: false },
        remove: { value: false }
    };

    // Respect read-only restrictions
    if (accessRestrictions.readOnly) {
        accessContext.post.value = false;
        accessContext.patch.value = false;
        accessContext.remove.value = false;
    }

    // Build an (extendable) relation schema; additionalProperties: true so other nodes can add props.
    const relationSchema = Type.Object(
        {},
        {
            title: relation,
            additionalProperties: true,
            'x-accessRestrictions': accessRestrictions,
            'x-accessContext': accessContext
        }
    );

    // subDataDictionary defines the namespace/relation structure for metadata assembly
    const subDataDictionary = {
        [namespace]: {
            [relation]: relationSchema
        }
    };

    // MenuButton does not contribute local properties to the form
    const partialTypeDefinition = {};

    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
